import { Link } from 'react-router-dom';
import '../App.css';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Glasses, Aperture, Sun, CheckCircle2, Droplets, ArrowRight, FileText } from 'lucide-react';
import { CollectionCard } from '../components/CollectionCard';
import { GoogleReviews } from '../components/GoogleReviews';
import { NavBar } from '../components/NavBar';
import { Footer } from '../components/Footer';
import { PageHead } from '../components/PageHead';
import { StructuredData, breadcrumb, webpage } from '../components/StructuredData';

const heroSlides = [
  { src: '/slide02.jpg', alt: 'Intérieur de la Lunetterie du Russey – sélection de montures françaises' },
  { src: '/slide01.jpg', alt: 'Vitrine de la Lunetterie du Russey, opticien au pôle santé du Russey (25210)' },
  { src: '/slide03.jpg', alt: 'Espace collections de la Lunetterie du Russey – montures et lunettes solaires' },
];

export function HomePage() {
  return (
    <div className="App">
      <PageHead
        title="Lunetterie du Russey – Opticien indépendant au Russey (25210) Haut-Doubs"
        description="Votre opticienne indépendante au pôle santé du Russey depuis 2014. Montures françaises Origine France Garantie, verres Essilor, lunettes solaires. Prise de RDV en ligne."
        image="/slide02.jpg"
      />
      <StructuredData data={webpage({
        name: 'Lunetterie du Russey – Opticien indépendant au Russey (25210)',
        description: 'Votre opticienne indépendante au pôle santé du Russey depuis 2014.',
        path: '/',
      })} />
      <NavBar />

      {/* Hero */}
      <section id="accueil" className="hero-section">
        <div className="hero-overlay" />
        <div className="hero-content">
          <Badge className="hero-badge">Haut-Doubs · Pays Horloger</Badge>
          <h1 className="hero-title">Votre opticien au Russey</h1>
          <p className="hero-subtitle">
            Opticienne diplômée depuis 18 ans · Savoir-faire artisanal · Sélection française
          </p>
          <div className="hero-buttons" role="group" aria-label="Vos besoins optique">
            <Button asChild size="lg" className="hero-cta">
              <Link to="/lunettes">
                <Glasses className="h-5 w-5 shrink-0" aria-hidden />
                J'ai besoin de lunettes
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="hero-cta-secondary">
              <Link to="/lentilles">
                <Droplets className="h-5 w-5 shrink-0" aria-hidden />
                J'ai besoin de lentilles
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Carrousel photos */}
      <div className="hero-carousel" aria-label="Galerie photos">

        <div className="hero-carousel-track">
          {[...heroSlides, ...heroSlides].map((slide, i) => (
            <img
              key={i}
              src={slide.src}
              alt={slide.alt}
              className="hero-carousel-slide"
              loading={i < 3 ? 'eager' : 'lazy'}
              fetchpriority={i === 0 ? 'high' : 'auto'}
            />
          ))}
        </div>

        {/* Vague bas — raccord avec la section beige */}
        <svg className="carousel-wave-bottom" viewBox="0 0 1440 60"
          preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path d="M0,38 C240,12 480,56 720,28 C960,0 1200,50 1440,22 L1440,60 L0,60 Z"
            fill="#e8e4d8" />
        </svg>

      </div>

      {/* Services teaser */}
      <section className="collections-section-new wave-bottom-to-dark">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Comment pouvons-nous vous aider ?</h2>
            <p className="section-description" style={{ color: '#2c3e50', fontWeight: 500 }}>
              Choisissez votre besoin pour être orienté vers le bon parcours
            </p>
          </div>

          <div className="collections-grid-new">
            <CollectionCard
              title="Lunettes de vue"
              color="orange"
              icon={<Glasses size={28} />}
              intro="Premier équipement, renouvellement ou simple envie de changement ? Trouvez le bon parcours selon votre situation en quelques clics."
              path="/lunettes"
            />
            <CollectionCard
              title="Lentilles de contact"
              color="orange"
              icon={<Droplets size={28} />}
              intro="Première paire, renouvellement d'ordonnance ou commande à renouveler : choisissez votre porte d'entrée sans prise de tête."
              path="/lentilles"
            />
            <CollectionCard
              title="Validité d'ordonnance"
              color="orange"
              icon={<FileText size={28} />}
              intro="Vérifiez en quelques secondes si votre ordonnance est encore valable, selon votre âge et les règles légales en vigueur."
              path="/ordonnance"
            />
          </div>
        </div>
      </section>

      {/* Atelier teaser */}
      <section className="atelier-section wave-bottom-to-cream">
        <div className="atelier-content">
          <div className="atelier-text">
            <Badge className="section-badge">Votre Opticien Expert</Badge>
            <h2 className="atelier-title">Un Savoir-Faire Artisanal au Cœur du Haut-Doubs</h2>
            <p className="atelier-description">
              Magasin indépendant, opticienne diplômée depuis 18 ans à votre service. Vaste sélection
              de montures françaises, labellisées "Origine France Garantie". Partenaire Essilor,
              nouveau progressif XR Design à découvrir.
            </p>
            <Separator className="my-6" style={{ backgroundColor: 'rgba(255,255,255,0.2)' }} />
            <div className="atelier-features">
              <div className="atelier-feature">
                <div className="atelier-feature-number">18</div>
                <div className="atelier-feature-label">Années d'expérience</div>
              </div>
              <div className="atelier-feature">
                <div className="atelier-feature-number">100%</div>
                <div className="atelier-feature-label">Indépendant</div>
              </div>
              <div className="atelier-feature">
                <div className="atelier-feature-number">FR</div>
                <div className="atelier-feature-label">Made in France</div>
              </div>
            </div>
            <Button asChild className="mt-8 bg-[var(--color-rust)] hover:opacity-90 text-white">
              <Link to="/atelier">
                En savoir plus sur l'atelier <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
          <div className="atelier-images">
            <img
              src="https://images.pexels.com/photos/5715905/pexels-photo-5715905.jpeg?w=600"
              alt="Opticienne au travail – ajustement de montures à la Lunetterie du Russey"
              className="atelier-image atelier-image-1"
            />
            <img
              src="/photo-elise.jpg"
              alt="Élise, opticienne diplômée depuis 18 ans à la Lunetterie du Russey"
              className="atelier-image atelier-image-2"
            />
          </div>
        </div>
      </section>

      {/* Collections teaser */}
      <section className="lunettes-section wave-bottom-to-forest">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Collections & Verres d'Exception</h2>
            <p className="section-description" style={{ color: '#2c3e50', fontWeight: 500 }}>
              Montures françaises, verres haute performance et lunettes solaires
            </p>
          </div>

          <div className="collections-grid-new">
            <CollectionCard
              title="Sélection de Montures"
              color="orange"
              icon={<Glasses size={28} />}
              intro="Montures Made in France, labellisées Origine France Garantie. Découvrez Roussilhe, Lafont et bien d'autres."
              path="/montures"
            />
            <CollectionCard
              title="Nos Verres"
              color="orange"
              icon={<Aperture size={28} />}
              intro="Partenaire Essilor depuis 2014. Progressifs XR Design, verres amincis, traitements antireflet dernière génération."
              path="/verres"
            />
            <CollectionCard
              title="Espace Solaire"
              color="orange"
              icon={<Sun size={28} />}
              intro="Protection solaire adaptée à l'altitude du Haut-Doubs. Maui Jim, Julbo et Oakley en sélection."
              path="/solaires"
            />
          </div>

          <div className="features-list">
            <div className="feature-item">
              <CheckCircle2 className="feature-icon" />
              <span>Accepte toutes les mutuelles</span>
            </div>
            <div className="feature-item">
              <CheckCircle2 className="feature-icon" />
              <span>Montures adaptées aux enfants dès la naissance</span>
            </div>
            <div className="feature-item">
              <CheckCircle2 className="feature-icon" />
              <span>Adaptation de lentilles progressives</span>
            </div>
          </div>
        </div>
      </section>

      {/* Avis clients */}
      <section className="contact-section home-reviews-section wave-bottom-to-dark">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title" style={{ color: '#f5f1e8' }}>Ce que disent nos clients</h2>
          </div>
          <div className="home-reviews-wrapper">
            <GoogleReviews />
          </div>
          <div style={{ textAlign: 'center', marginTop: '2rem' }}>
            <Button asChild variant="outline" className="border-white/40 text-white hover:bg-white/10">
              <Link to="/contact">Voir les infos & horaires <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
